import { useNavigate } from '@tanstack/react-router';
import type { FocusEvent } from 'react';
import { useCardPopover } from '../../hooks/useCardPopover';
import { cn, getBillingTierColorClass, type TBillingTier } from '../../utils';
import { Badge } from '../Badge';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '../Popover';
import { Text } from '../Text';
import { ProjectCardPopover, type ProjectCardPopoverLinkProps } from './ProjectCardPopover';

export type ProjectCardProps = {
  name: string;
  billingTier?: TBillingTier;
  pmName?: string;
  projectId?: string;
  address?: string;
  linkProps?: ProjectCardPopoverLinkProps;
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
  onHover?: (projectId?: string) => void;
  isHover?: boolean;
  /** Whether the project is bookmarked (show filled vs outline icon in popover). */
  bookmarked?: boolean;
  /** Called when the bookmark icon in the popover is clicked. Omit to hide the bookmark button. */
  onBookmarkClick?: (e: React.MouseEvent) => void;
  /**
   * When provided: hover opens the popover, click closes it and calls this (e.g. fly to project on map).
   * When omitted: click toggles the popover (legacy behavior).
   */
  onFlyToClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
  /** When true, shows a SAMPLE badge (e.g. for onboarding sandbox project). */
  isSandboxProject?: boolean;
  /** When true, shows selected state (e.g. when map popup is open for this project). */
  selected?: boolean;
};

/**
 * ProjectCard displays a single project card in the map view list
 */
function ProjectCard({
  name,
  billingTier,
  pmName,
  projectId,
  address,
  linkProps,
  onFocus,
  onHover,
  isHover,
  bookmarked,
  onBookmarkClick,
  onFlyToClick,
  isSandboxProject,
  selected,
}: ProjectCardProps) {
  const navigate = useNavigate();
  const { isOpen, onOpenChange, rowProps, cardProps, contentProps } = useCardPopover({
    name,
    hoverId: projectId,
    onHover,
    onFocus,
    onFlyToClick,
  });
  const barClass = billingTier ? getBillingTierColorClass(billingTier) : 'bg-grey-40';

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (linkProps?.to) {
      navigate({ to: linkProps.to });
    }
  };

  const cardDiv = (
    <div
      role="button"
      tabIndex={0}
      {...cardProps}
      className={cn(
        'flex-1 p-2 flex flex-col gap-0.5 min-w-0  transition-colors cursor-pointer border border-transparent focus-ring rounded',
        'hover:bg-grey-10 focus:bg-blue-10 focus:border-focus',
        isHover && 'bg-grey-10',
        selected && 'bg-blue-10 border-blue-50 hover:bg-blue-10',
      )}
      onDoubleClick={handleDoubleClick}
      data-testid={linkProps?.['data-testid']}
    >
      {isSandboxProject && (
        <Badge color="blue" className="w-fit">
          Sample
        </Badge>
      )}
      <Text className="text-primary-dark text-sm font-medium">{name}</Text>
      {pmName != null && pmName !== '' && <Text className="text-grey-70 text-xs truncate">PM: {pmName}</Text>}
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <div {...rowProps} className="flex gap-1 border-b border-grey-20 pr-0.5 pt-0.5">
        {/* Colored vertical bar indicator */}
        <div className="flex flex-col items-center justify-center py-3 shrink-0">
          <div className={`w-1 rounded ${barClass} opacity-80 flex-1 min-h-[40px]`} />
        </div>
        {/* When onFlyToClick is set, use Anchor only so click doesn't toggle (we handle hover + click). Otherwise use Trigger for click-to-open. */}
        {onFlyToClick ? (
          <PopoverAnchor asChild>{cardDiv}</PopoverAnchor>
        ) : (
          <PopoverTrigger asChild>{cardDiv}</PopoverTrigger>
        )}
      </div>
      <PopoverContent {...contentProps} align="center" side="right" className="w-auto p-4" sideOffset={12}>
        <ProjectCardPopover
          name={name}
          billingTier={billingTier}
          pmName={pmName}
          projectId={projectId}
          address={address}
          linkProps={linkProps}
          bookmarked={bookmarked}
          onBookmarkClick={onBookmarkClick}
        />
      </PopoverContent>
    </Popover>
  );
}

export { ProjectCard };
